// Copyright 2026 Monterosa Productions Limited. All rights reserved.
import '@monterosa/sdk-storage-kit';

import { handleCallback, getAccessToken } from './auth.js';
import { getConsent, onConsentChange, toCategories } from './consent.js';
import { mountEmbed, sendToken, sendConsent, enableAnalytics } from './embed.js';
import { initOneTrust } from './onetrust.js';
import { applyOneTrustFonts, parseUploadedFonts, type UploadedFont } from './fonts.js';
import { config } from './config.js';

interface ParentSettings {
  headerLogoUrl?: string;
  oneTrustId?: string;
  ga4TrackingId?: string;
  analyticsDebugLogs?: boolean;
  uploadedFonts?: UploadedFont[];
  bodyFontFamily?: string;
  labelFontFamily?: string;
}

async function loadParentSettings(): Promise<ParentSettings> {
  const configRes = await fetch('/api/fantasy/config').catch(() => null);
  if (!configRes?.ok) return {};
  const body = (await configRes.json()) as { data?: { projectSettingsUrl?: string } };
  const settingsUrl = body.data?.projectSettingsUrl;
  if (!settingsUrl) return {};

  const settingsRes = await fetch(settingsUrl).catch(() => null);
  if (!settingsRes?.ok) return {};
  const json = (await settingsRes.json()) as Record<string, unknown>;

  const all = json.all;
  if (all && typeof all === 'object' && !Array.isArray(all)) {
    const settings = all as Record<string, string>;
    // Boolean settings may arrive as a real boolean or a "true"/"false" string.
    const debugRaw: unknown = settings.analytics_debugLogs;
    return {
      headerLogoUrl: settings.assets_parentHeaderLogo ?? undefined,
      oneTrustId: settings.consent_oneTrustId ?? undefined,
      ga4TrackingId: settings.analytics_ga4TrackingId ?? undefined,
      analyticsDebugLogs: debugRaw === true || debugRaw === 'true',
      uploadedFonts: parseUploadedFonts(settings.uploadedFonts),
      bodyFontFamily: settings.css_variables_body_fontFamily ?? undefined,
      labelFontFamily: settings.css_variables_label_fontFamily ?? undefined,
    };
  }
  return {};
}

function applyParentHeaderLogo(url: string): void {
  const img = document.getElementById('parent-header-logo') as HTMLImageElement | null;
  const link = document.getElementById('parent-header-logo-link') as HTMLAnchorElement | null;
  if (img) img.src = url;
  if (link) link.style.display = 'inline-block';
}

async function boot(): Promise<void> {
  if (location.search.includes('code=')) {
    try {
      await handleCallback();
    } catch (err) {
      console.error('[parent] OIDC callback failed:', err);
    }
    location.replace(location.pathname);
    return;
  }

  const container = document.getElementById('embed-container')!;

  void loadParentSettings().then(({ headerLogoUrl, oneTrustId, ga4TrackingId, analyticsDebugLogs, uploadedFonts, bodyFontFamily, labelFontFamily }) => {
    if (headerLogoUrl) applyParentHeaderLogo(headerLogoUrl);
    if (oneTrustId) {
      initOneTrust(oneTrustId);
      // Load the operator's fonts and restyle the OneTrust banner/modal to match
      // the embed once they're ready.
      void applyOneTrustFonts({ uploadedFonts, bodyFontFamily, labelFontFamily });
    }
    // Prefer the operator-configured GA Tracking ID; fall back to the build-time
    // config for backward compatibility. GA4 only loads once analytics consent
    // is granted (see enableAnalytics / ensureGA4).
    const trackingId = ga4TrackingId ?? config.ga4TrackingId;
    if (trackingId) enableAnalytics(trackingId, analyticsDebugLogs ?? false);
  });

  await mountEmbed(container);

  // Send current consent categories (OneTrust populates these via saveConsent).
  sendConsent(toCategories(getConsent()));
  onConsentChange((prefs) => sendConsent(toCategories(prefs)));

  const accessToken = await getAccessToken();
  if (accessToken) await sendToken(accessToken);
}

boot().catch(console.error);
